import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { Car, Target, DollarSign, Clock, Shield, CheckCircle, AlertTriangle, Wrench, Droplets, Settings, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import windshieldServices from '../../assets/WindshieldServices.jpg';

export default function WindshieldReplacement() {
	return (
		<div>
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				className="bg-gradient-to-b from-white to-babyblue/20 border-b gradient-hero"
			>
				<div className="container py-12 md:py-16">
					<div className="grid md:grid-cols-2 gap-12 items-center">
						<motion.div
							initial={{ opacity: 0, x: -30 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
						>
							<motion.h1 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.3 }}
								className="text-3xl md:text-4xl font-extrabold text-gradient"
							>
								Windshield Replacement
							</motion.h1>
							<motion.p 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.4 }}
								className="mt-3 max-w-3xl text-gray-600"
							>
								Your windshield is a vital part of your vehicle's structure and safety system. At Ubiquity AutoGlass, we specialize in providing reliable, high-quality windshield replacement services designed to restore your vehicle's integrity and get you back on the road with confidence.
							</motion.p>
							<motion.div 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.5 }}
								className="mt-6 flex gap-3"
							>
								<motion.a 
									href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" 
									target="_blank" 
									rel="noreferrer"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									<Button variant="gradient">Get A Quote!</Button>
								</motion.a>
								<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
									<Link to="/support"><Button variant="outline">Contact Us</Button></Link>
								</motion.div>
							</motion.div>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.8, delay: 0.6 }}
							className="flex justify-center"
						>
							<img src={windshieldServices} alt="Windshield replacement services" className="w-full max-w-lg rounded-lg shadow-lg" />
						</motion.div>
					</div>
				</div>
			</motion.section>

			{/* Installation Process Steps */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="container py-12 md:py-16"
			>
				<motion.h2 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="text-2xl md:text-3xl font-bold text-center mb-12"
				>
					Our Windshield Replacement Process
				</motion.h2>
				<motion.div 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					className="grid md:grid-cols-2 lg:grid-cols-5 gap-8"
				>
				<motion.div 
					initial={{ opacity: 0, scale: 0.8 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.4 }}
					whileHover={{ scale: 1.05 }}
					className="text-center"
				>
					<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
						<Wrench className="w-8 h-8 text-primary" />
					</div>
					<h3 className="text-lg font-semibold mb-3">Removal of Windshield</h3>
					<p className="text-gray-600 text-sm">Our technicians begin by carefully removing the damaged or broken windshield using specialized tools to ensure no harm comes to the vehicle's paint, body, cameras or other vehicle components.</p>
				</motion.div>
				<motion.div 
					initial={{ opacity: 0, scale: 0.8 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.5 }}
					whileHover={{ scale: 1.05 }}
					className="text-center"
				>
					<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
						<Droplets className="w-8 h-8 text-primary" />
					</div>
					<h3 className="text-lg font-semibold mb-3">Install Preparation</h3>
					<p className="text-gray-600 text-sm">The old adhesive is removed to create a clean surface for the new glass installation. We decontaminate the surface by removing old adhesive, treating visible metal with rust dissolving acid, and priming areas where adhesive will be applied.</p>
				</motion.div>
				<motion.div 
					initial={{ opacity: 0, scale: 0.8 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.6 }}
					whileHover={{ scale: 1.05 }}
					className="text-center"
				>
					<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
						<Settings className="w-8 h-8 text-primary" />
					</div>
					<h3 className="text-lg font-semibold mb-3">Applying Adhesive</h3>
					<p className="text-gray-600 text-sm">High-quality urethane adhesive is meticulously applied to the body of the vehicle using advanced tools. The adhesive is evenly distributed for maximum bonding strength, ensuring a durable and leak-proof seal.</p>
				</motion.div>
				<motion.div 
					initial={{ opacity: 0, scale: 0.8 }}
					whileInView={{ opacity: 1, scale: 1 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.7 }}
					whileHover={{ scale: 1.05 }}
					className="text-center"
				>
					<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
						<Car className="w-8 h-8 text-primary" />
					</div>
					<h3 className="text-lg font-semibold mb-3">Windshield Installation</h3>
					<p className="text-gray-600 text-sm">The new windshield is carefully aligned and installed onto the prepared surface. Our technicians use precise positioning to ensure a perfect fit, followed by pressure application to secure the glass in place.</p>
				</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.8 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<CheckCircle className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Peace Of Mind</h3>
						<p className="text-gray-600 text-sm">We provide a lifetime warranty on all our services, ensuring any issues are promptly resolved. Every job and vehicle we service is protected by extensive third-party insurance.</p>
					</motion.div>
				</motion.div>
			</motion.section>

			{/* Safety & Security Section */}
			<section className="bg-gray-50 py-12 md:py-16">
				<div className="container">
					<h3 className="text-2xl md:text-3xl font-bold text-center mb-12">Safety & Security</h3>
					<p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">Our technicians follow strict installation standards that go above and beyond government safety regulations to ensure your safety. We also use advanced technology to double-check that our procedures are followed correctly, giving you peace of mind.</p>
					<div className="grid md:grid-cols-3 gap-8">
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Shield className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Advanced Installation Standards</h4>
							<p className="text-gray-600 text-sm">Ubiquity strictly adheres to installation standards that meet or exceed the safety regulations set by the National Highway Traffic Safety Administration (NHTSA) and the Federal Motor Vehicle Safety Standards (FMVSS).</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<CheckCircle className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Lifetime Warranty</h4>
							<p className="text-gray-600 text-sm">Ubiquity offers a lifetime workmanship warranty, and is covered by 3rd party insurers on all services we provide. This means that if there are any issues with the installation or the glass, we will take care of it.</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Settings className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Technology-Driven Safety Measures</h4>
							<p className="text-gray-600 text-sm">Ubiquity deployed Frontline on iOS / Android to ensure precise installation procedures, minimizing errors and enhancing safety. Frontline automates identity verification, background checks, and insurance coverage.</p>
						</div>
					</div>
				</div>
			</section>

			{/* Signs You Need Replacement */}
			<section className="container py-12 md:py-16">
				<h3 className="text-2xl md:text-3xl font-bold text-center mb-12">Signs You Need a Windshield Replacement</h3>
				<div className="grid md:grid-cols-2 gap-12">
					<div>
						<p className="text-gray-600 mb-6">If you notice any of the following, it may be time to replace your windshield:</p>
						<ul className="space-y-3">
							<li className="flex items-start gap-3">
								<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600"><strong>Cracks longer than 6 inches</strong> or that obstruct your view.</span>
							</li>
							<li className="flex items-start gap-3">
								<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600"><strong>Damage in the driver's line of sight</strong>, impairing visibility.</span>
							</li>
							<li className="flex items-start gap-3">
								<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600"><strong>Edge cracks</strong> that compromise the windshield's structural integrity.</span>
							</li>
							<li className="flex items-start gap-3">
								<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600"><strong>Multiple chips or widespread damage</strong> that cannot be repaired.</span>
							</li>
						</ul>
					</div>
					<div>
						<div className="bg-red-50 border border-red-200 rounded-lg p-6">
							<h4 className="text-lg font-semibold text-red-800 mb-3">Important Safety Note</h4>
							<p className="text-red-700">A damaged windshield can affect your vehicle's safety features, including airbags and structural support. Replacing it promptly is essential to ensure your protection.</p>
						</div>
					</div>
				</div>
			</section>

			{/* Benefits */}
			<section className="bg-gray-50 py-12 md:py-16">
				<div className="container">
					<h3 className="text-2xl md:text-3xl font-bold text-center mb-12">Benefits of Windshield Replacement</h3>
					<div className="grid md:grid-cols-4 gap-8">
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Shield className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Safety</h4>
							<p className="text-gray-600 text-sm">Restores the structural integrity of your vehicle, protecting you in the event of an accident.</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Eye className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Optimal Visibility</h4>
							<p className="text-gray-600 text-sm">A new windshield provides clear, unobstructed views of the road.</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Clock className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Long-Term Durability</h4>
							<p className="text-gray-600 text-sm">High-quality materials ensure your windshield stands the test of time.</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<CheckCircle className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Compliance</h4>
							<p className="text-gray-600 text-sm">Ensures your vehicle meets state and federal safety standards.</p>
						</div>
					</div>
				</div>
			</section>

			{/* Mobile Services */}
			<section className="container py-12 md:py-16">
				<div className="text-center">
					<h3 className="text-2xl md:text-3xl font-bold mb-6">Mobile Windshield Replacement Services</h3>
					<p className="text-gray-600 mb-8 max-w-2xl mx-auto">Can't make it to our shop? No problem! With <strong>Ubiquity AutoGlass Mobile Services</strong>, our technicians can replace your windshield wherever you are, whether at home, work, or on the go.</p>
				</div>
			</section>

			<section className="animated-gradient particle-container subtle-glow">
				<div className="container py-12 md:py-16 text-white text-center">
					<h3 className="text-2xl font-bold mb-4">Schedule Your Windshield Replacement Today</h3>
					<p className="mb-6 max-w-2xl mx-auto">Don't let a damaged windshield compromise your safety. Trust <strong>Ubiquity AutoGlass</strong> for professional windshield replacements that prioritize your security and peace of mind.</p>
					<div className="flex gap-3 justify-center">
						<a href="https://realm.ubiquityauto.com/order/2dcf3188-0a07-4444-b5f8-065a5dc238bb?access_token=_ha6LYxaO-9HjbYZVCY7P21vuKFFRaQ5" target="_blank" rel="noreferrer"><Button className="bg-white text-secondary hover:opacity-90">Get A Quote!</Button></a>
						<Link to="/support"><Button variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">Contact Us</Button></Link>
					</div>
					<p className="mt-6 text-sm opacity-90">Ready for a safer, clearer drive? Get Started!</p>
				</div>
			</section>
		</div>
	);
}
